import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X, Mail, MapPin, Award, Network, Shield, Cloud, Code, Phone, Monitor, Database, Smartphone, Server, Wifi, Lock, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Services = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const coreServices = [
    {
      icon: Network,
      title: 'Network Management & Installation',
      description: 'Comprehensive network infrastructure development, fiber optic installation, and network security solutions.',
      features: ['Network Design', 'Fiber Optic Installation', 'Infrastructure Setup', 'Network Monitoring']
    },
    {
      icon: Shield,
      title: 'Network Security & Cybersecurity',
      description: 'Advanced cybersecurity solutions with robust access control management systems and threat detection.',
      features: ['Firewall Management', 'Intrusion Detection', 'Security Audits', 'Access Control']
    },
    {
      icon: Cloud,
      title: 'Cloud Management Solutions',
      description: 'Scalable cloud solutions and managed IT services for optimal business operations.',
      features: ['Cloud Migration', 'Azure Management', 'AWS Solutions', 'Cloud Security']
    },
    {
      icon: Code,
      title: 'Software Development',
      description: 'Microsoft SharePoint, Dynamics 365, custom applications, and bespoke software solutions.',
      features: ['SharePoint Development', 'Dynamics 365', 'Custom Applications', 'Web Development']
    },
    {
      icon: Phone,
      title: 'VoIP & Telecommunication',
      description: 'Digital IP/PBX services, VoIP solutions, and broadband internet communication systems.',
      features: ['VoIP Implementation', 'IP/PBX Setup', 'Telephony Systems', 'Video Conferencing']
    },
    {
      icon: Monitor,
      title: 'CCTV & Surveillance',
      description: 'Complete security surveillance systems installation, monitoring, and management.',
      features: ['CCTV Installation', 'Remote Monitoring', 'DVR/NVR Setup', '24/7 Surveillance']
    },
    {
      icon: Database,
      title: 'Document Management Systems',
      description: 'Enterprise document management, workflow automation, and data processing solutions.',
      features: ['Document Management', 'Workflow Automation', 'Data Processing', 'Archive Solutions']
    },
    {
      icon: Smartphone,
      title: 'Enterprise Mobility',
      description: 'Mobile solutions, fleet management software, POS systems, and enterprise applications.',
      features: ['Mobile Apps', 'Fleet Management', 'POS Systems', 'Enterprise Mobility']
    },
    {
      icon: Server,
      title: 'IT Managed Services',
      description: 'Complete IT support, maintenance, troubleshooting, and managed services with SLA.',
      features: ['24/7 Support', 'Remote Assistance', 'Onsite Support', 'SLA Management']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/0iy7o1s0_cropped-HYPERLINK_transperat.webp"
                alt="HNS Logo"
                className="h-14 w-14 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-[#18246F]">HNS</h1>
                <p className="text-xs text-gray-600">...technology with infinite solutions</p>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Home</Link>
              <Link to="/about" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">About</Link>
              <Link to="/services" className="text-[#18246F] font-semibold">Services</Link>
              <Link to="/projects" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Projects</Link>
              <Link to="/team" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Team</Link>
              <Link to="/contact" className="text-gray-700 hover:text-[#18246F] transition-colors font-medium">Contact</Link>
              <Button onClick={() => navigate('/contact')} className="bg-[#3C74FF] hover:bg-[#2d5fd9] text-white">Let's Connect</Button>
            </nav>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-gray-700">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Home</Link>
              <Link to="/about" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">About</Link>
              <Link to="/services" className="block px-4 py-2 text-[#18246F] bg-gray-100 rounded-lg font-semibold">Services</Link>
              <Link to="/projects" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Projects</Link>
              <Link to="/team" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Team</Link>
              <Link to="/contact" className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Contact</Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#18246F] via-[#293074] to-[#3d4a8f] text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="mb-4 bg-white/20 text-white border-white/30">Our Services</Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Comprehensive IT Solutions</h1>
          <p className="text-xl text-white/90 max-w-3xl">From network infrastructure to software development, we deliver cutting-edge technology solutions tailored to your business needs</p>
        </div>
      </div>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#18246F] mb-4">Complete Technology <span className="text-[#3C74FF]">Services</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Delivering excellence across all aspects of IT infrastructure and business solutions</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 border-gray-100 hover:border-[#3C74FF] hover:shadow-2xl transition-all duration-300 group">
                  <CardHeader>
                    <div className="bg-gradient-to-br from-[#3C74FF]/10 to-[#616EB7]/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-4 group-hover:from-[#3C74FF] group-hover:to-[#616EB7] transition-all duration-300">
                      <Icon className="text-[#3C74FF] group-hover:text-white transition-colors" size={32} />
                    </div>
                    <CardTitle className="text-xl group-hover:text-[#3C74FF] transition-colors">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <div className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center text-sm text-gray-700">
                          <CheckCircle size={16} className="text-[#3C74FF] mr-2 flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Microsoft Services */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-[#3C74FF]/10 text-[#3C74FF] border-[#3C74FF]/20">Microsoft Partner</Badge>
              <h2 className="text-4xl font-bold text-[#18246F] mb-6">Microsoft Solutions Expertise</h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                As a certified Microsoft Partner, we specialize in delivering comprehensive Microsoft solutions including SharePoint, Dynamics 365, Office 365, and Azure cloud services.
              </p>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#3C74FF] flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-700">Microsoft SharePoint Development & Customization</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#3C74FF] flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-700">Dynamics 365 Implementation & Support</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#3C74FF] flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-700">Office 365 Migration & Management</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#3C74FF] flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-700">Azure Cloud Solutions & Services</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#3C74FF] to-[#616EB7] rounded-3xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">Why Choose Our Services?</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="bg-white/20 rounded-lg p-2 mr-3">
                    <Award size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Certified Professionals</h4>
                    <p className="text-white/90 text-sm">Expert team with industry certifications</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-white/20 rounded-lg p-2 mr-3">
                    <Shield size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Quality Assurance</h4>
                    <p className="text-white/90 text-sm">ISO 9001:2015 certified processes</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-white/20 rounded-lg p-2 mr-3">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Proven Track Record</h4>
                    <p className="text-white/90 text-sm">150+ successful project deliveries</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#18246F] mb-4">Ready to Transform Your IT?</h2>
          <p className="text-gray-600 mb-8">Let's discuss how our services can help your business grow</p>
          <Button 
            onClick={() => navigate('/#contact')}
            size="lg"
            className="bg-[#3C74FF] hover:bg-[#2d5fd9] text-white group"
          >
            Request Consultation
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#18246F] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_hyperlink-network/artifacts/0iy7o1s0_cropped-HYPERLINK_transperat.webp"
                  alt="HNS Logo"
                  className="h-12 w-12 object-contain"
                />
                <div>
                  <h3 className="text-xl font-bold">HNS</h3>
                  <p className="text-xs text-gray-300">...technology with infinite solutions</p>
                </div>
              </Link>
              <p className="text-sm text-gray-300 leading-relaxed mb-4">
                Leading the ICT industry with reliable information systems solutions. ISO 9001:2015 certified with over 20 years of excellence in technology delivery.
              </p>
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <Award size={16} />
                <span>Trusted by 50+ Organizations</span>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Services</Link></li>
                <li><Link to="/projects" className="hover:text-white transition-colors">Projects</Link></li>
                <li><Link to="/team" className="hover:text-white transition-colors">Team</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li>Network Security</li>
                <li>Cloud Management</li>
                <li>Software Development</li>
                <li>VoIP Solutions</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start">
                  <Mail size={16} className="mr-2 mt-1 flex-shrink-0" />
                  <span>info@hnsgh.com</span>
                </li>
                <li className="flex items-start">
                  <MapPin size={16} className="mr-2 mt-1 flex-shrink-0" />
                  <span>Accra, Ghana | Manassas, VA USA</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Hyperlink Network Systems Limited. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Call Button */}
      <a 
        href="tel:+15714064203"
        className="fixed bottom-8 left-8 bg-[#3C74FF] hover:bg-[#2d5fd9] text-white rounded-full p-4 shadow-2xl z-50 flex items-center gap-3 group transition-all duration-300 hover:scale-110"
      >
        <Phone size={24} className="animate-pulse" />
        <span className="hidden group-hover:inline-block text-sm font-semibold whitespace-nowrap">
          +1 (571) 406-4203
        </span>
      </a>
    </div>
  );
};

export default Services;
